import { inject, Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Observable, of } from 'rxjs';
import { auditTime, map, shareReplay } from 'rxjs/operators';

export interface WindowSize {
    width: number;
    height: number;
}

@Injectable({
    providedIn: 'root',
})
export class WindowResizeService {
    private zone = inject(NgZone);

    public static readonly RESIZE_AUDIT_MS = 250;

    private readonly resizeEvents$: Observable<WindowSize>;

    constructor() {
        if (typeof window === 'undefined') {
            this.resizeEvents$ = of({ width: 0, height: 0 });
            return;
        }

        // Create resize stream outside Angular to avoid excessive change detection while dragging the window
        this.resizeEvents$ = new Observable<WindowSize>(subscriber => {
            this.zone.runOutsideAngular(() => {
                const sub = fromEvent(window, 'resize')
                    .pipe(
                        map(() => ({ width: window.innerWidth, height: window.innerHeight })),
                        auditTime(WindowResizeService.RESIZE_AUDIT_MS),
                    )
                    .subscribe(subscriber);

                return () => sub.unsubscribe();
            });
        }).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    }

    public onResize(): Observable<WindowSize> {
        // Emit current size immediately (unthrottled), then emit audited resize events.
        const initial = typeof window === 'undefined' ? of({ width: 0, height: 0 }) : of({ width: window.innerWidth, height: window.innerHeight });
        return merge(initial, this.resizeEvents$);
    }
}
